import { BarChart3, CheckCircle2, LockKeyhole, Trophy } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";
import "./GameFormatPoll.css";

export const gameFormatPollQuery = () => api.get("/polls/game-format").then((result) => result.data);

function PollResults({ options }) {
  const total = options.reduce((sum, option) => sum + option.count, 0);

  return (
    <div className="poll-results" data-testid="poll-public-results">
      {options.map((option) => {
        const percentage = total ? Math.round((option.count / total) * 100) : 0;
        return (
          <div className="poll-result" key={option.id} data-testid={`poll-result-${option.id}`}>
            <div>
              <strong>{option.label}</strong>
              <span data-testid={`poll-count-${option.id}`}>{option.count} voto(s)</span>
            </div>
            <div className="poll-meter" aria-label={`${option.label}: ${percentage}%`}>
              <i style={{ width: `${percentage}%` }} />
            </div>
          </div>
        );
      })}
      <small data-testid="poll-total-votes">{total} participação(ões)</small>
    </div>
  );
}

function AdminResponses() {
  const responsesQuery = useQuery({
    queryKey: ["game-format-poll-admin-responses"],
    queryFn: () => api.get("/admin/polls/game-format/responses").then((result) => result.data),
  });
  const responses = responsesQuery.data?.responses || [];

  return (
    <details className="poll-admin-responses" data-testid="poll-admin-responses">
      <summary data-testid="toggle-poll-admin-responses">
        <LockKeyhole size={14} />
        Ver quem votou
      </summary>
      <div className="poll-voter-list" data-testid="poll-voter-list">
        {responses.map((response) => (
          <div className="poll-voter" key={response.user_id} data-testid={`poll-voter-${response.user_id}`}>
            <strong>{response.name}</strong>
            <span>{response.option_label}</span>
          </div>
        ))}
      </div>
    </details>
  );
}

export function GameFormatPoll({ blocking = false, poll: suppliedPoll }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const ownQuery = useQuery({
    queryKey: ["game-format-poll"],
    queryFn: gameFormatPollQuery,
    enabled: !suppliedPoll,
  });
  const poll = suppliedPoll || ownQuery.data;
  const [submitting, setSubmitting] = useState(false);

  const vote = async (option) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/polls/game-format/vote", { option });
      queryClient.setQueryData(["game-format-poll"], data);
      queryClient.invalidateQueries({ queryKey: ["game-format-poll-admin-responses"] });
      toast.success("Seu voto foi registrado.");
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSubmitting(false);
    }
  };

  if (!poll || ownQuery.isLoading) {
    return <section className="poll-card poll-loading" data-testid="poll-loading">Carregando enquete...</section>;
  }

  return (
    <section className={`poll-card ${blocking ? "poll-card-blocking" : ""}`} data-testid="game-format-poll">
      <div className="poll-heading">
        <span><Trophy size={15} /> ENQUETE DA PELADA</span>
        {poll.has_responded && <CheckCircle2 size={19} data-testid="poll-vote-confirmed" />}
      </div>
      <h2>{poll.question}</h2>
      {!poll.has_responded && (
        <div className="poll-options" data-testid="poll-vote-options">
          {poll.options.map((option) => (
            <button
              disabled={submitting}
              key={option.id}
              onClick={() => vote(option.id)}
              data-testid={`poll-vote-${option.id}`}
            >
              <span>{option.id === "pontos_corridos" ? "01" : "02"}</span>
              {option.label}
            </button>
          ))}
        </div>
      )}
      {poll.has_responded && <p className="poll-confirmation" data-testid="poll-confirmation">Voto registrado. Acompanhe o resultado abaixo.</p>}
      <PollResults options={poll.options} />
      {user?.role === "admin" && poll.has_responded && <AdminResponses />}
      {!poll.has_responded && blocking && <p className="poll-required-copy">Escolha uma opção para entrar na pelada.</p>}
    </section>
  );
}